import React from 'react';
import { Briefcase, MapPin, Calendar, TrendingUp, Award } from 'lucide-react';
import { experience } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Experience = () => {
  const colors = ['#10b981', '#06b6d4'];

  return (
    <section id="experience" className="py-20 bg-[#121214] relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#06b6d4] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <Briefcase size={40} className="text-[#06b6d4]" />
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Professional <span className="text-[#06b6d4]">Journey</span>
              </h2>
            </div>
            <div className="w-24 h-1 bg-gradient-to-r from-[#06b6d4] to-[#10b981] mx-auto mb-4"></div>
            <p className="text-[#a3a3a3] max-w-2xl mx-auto">
              My professional experience and contributions to impactful projects
            </p>
          </div>

          {/* Experience Cards Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {experience.map((exp, index) => {
              const accentColor = colors[index % colors.length];
              
              return (
                <Card
                  key={exp.id}
                  className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#06b6d4] transition-all duration-300 group relative overflow-hidden"
                >
                  {/* Accent Bar */}
                  <div 
                    className="absolute top-0 left-0 right-0 h-1 transition-all duration-300 group-hover:h-2"
                    style={{ backgroundColor: accentColor }}
                  ></div>

                  <CardContent className="p-6 pt-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <Badge className="bg-[#06b6d4]/20 text-[#06b6d4] border-[#06b6d4] mb-3">
                          {exp.type}
                        </Badge>
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#06b6d4] transition-colors duration-300">
                          {exp.title}
                        </h3>
                        <p className="text-[#10b981] text-lg font-semibold flex items-center gap-2">
                          <Briefcase size={16} />
                          {exp.company}
                        </p>
                      </div>
                      
                      {/* Icon */}
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center bg-[#121214] group-hover:scale-110 transition-transform duration-300"
                        style={{ color: accentColor }}
                      >
                        <TrendingUp size={24} />
                      </div>
                    </div>

                    {/* Meta Information */}
                    <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-[#1f1f23]">
                      <div className="flex items-center gap-2 text-[#a3a3a3] text-sm">
                        <Calendar size={14} className="text-[#06b6d4]" />
                        <span>{exp.duration}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#a3a3a3] text-sm">
                        <MapPin size={14} className="text-[#10b981]" />
                        <span>{exp.location}</span>
                      </div>
                    </div>

                    {/* Key Achievements */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                        <Award size={16} className="text-[#f97316]" />
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {exp.responsibilities.map((resp, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[#a3a3a3] text-sm">
                            <div 
                              className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                              style={{ backgroundColor: accentColor }}
                            ></div>
                            <span className="leading-relaxed">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Hover Effect Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 h-0 group-hover:h-1 transition-all duration-300 bg-gradient-to-r from-[#10b981] to-[#06b6d4]"></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#10b981] transition-all duration-300 text-center group">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-[#10b981] mb-2 group-hover:scale-110 transition-transform duration-300">
                  2+
                </div>
                <div className="text-[#a3a3a3] text-sm">Months of Experience</div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#06b6d4] transition-all duration-300 text-center group">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-[#06b6d4] mb-2 group-hover:scale-110 transition-transform duration-300">
                  2
                </div>
                <div className="text-[#a3a3a3] text-sm">Companies Worked</div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#f97316] transition-all duration-300 text-center group">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-[#f97316] mb-2 group-hover:scale-110 transition-transform duration-300">
                  100%
                </div>
                <div className="text-[#a3a3a3] text-sm">Remote Work</div>
              </CardContent>
            </Card>

            <Card className="bg-[#0a0a0b] border-[#1f1f23] hover:border-[#10b981] transition-all duration-300 text-center group">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-[#10b981] mb-2 group-hover:scale-110 transition-transform duration-300">
                  5+
                </div>
                <div className="text-[#a3a3a3] text-sm">Technologies Used</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
